/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useState
} from 'react';
import {AutomationRuleTriggerContextType, ChecklistTriggerInputDataType, ChecklistTriggerType, DropdownStateType, FieldErrors, FieldValueChangedInputDataType, FieldValueChangedTriggerType, TriggerItemType, WorkItemAssignedToInputDataType, WorkItemIsAssignedToTriggerType, WorkItemTransitionsInputDataType, WorkItemTransitionTriggerType} from './types';
import { stringRequired } from '../utils/validations';
import useFieldValueChanged from './field-value-changed-form/use-field-value-changed';
import useAssignedToForm from './assigned-to-form/use-assigned-to-form';
import useWorkItemTransition from './work-item-transitions/use-work-item-transitions';
import { useAzureProvider } from 'src/azure/context';
import { useCheckListTrigger } from './checklist/use-check-list-trigger';

export const triggerTypes = {
	checkListIsCompleted: 'when-check-list-is-completed',
	checkListIsNotCompleted: 'when-check-list-is-not-completed',
	fieldValueIsChanged: 'field-value-changed',
	workItemIsUpdated: 'work-item-is-updated',
	workItemIsCreated: 'work-item-is-created',
	workItemTransitions: 'work-item-transitions',
	workItemIsAssignedTo: 'work-item-is-assigned-to'
};

export const automationRuleTriggerOptions = [
	{ label: 'Field value is changed', value: triggerTypes.fieldValueIsChanged },
	{ label: 'Work item updated', value: triggerTypes.workItemIsUpdated },
	{ label: 'Work item created', value: triggerTypes.workItemIsCreated },
	{ label: 'Work item transitioned', value: triggerTypes.workItemTransitions },
	{ label: 'Work item assigned', value: triggerTypes.workItemIsAssignedTo },
	{ label: 'Checklist is completed', value: triggerTypes.checkListIsCompleted },
	{ label: 'Checklist is not completed', value: triggerTypes.checkListIsNotCompleted },
];

const initialTriggerValues: TriggerItemType = {
	workItemTransitions: null,
	workItemIsAssignedTo: null,
	fieldValueChange: null,
	checklistTrigger: null
};

const AutomationRuleTriggerContext = createContext({} as AutomationRuleTriggerContextType);

export const useAutomationRuleTrigger = () => useContext(AutomationRuleTriggerContext);

export default function AutomationRuleTriggerProvider(props: {children?: ReactNode | Iterable<ReactNode>}) {
	const {applicationContext} = useAzureProvider();
	const [trigger, setTrigger] = useState<DropdownStateType>('');
	const [triggerItem, setTriggerItem] = useState<TriggerItemType | null>(null);

	const [validationErrors, setValidationErrors] = useState<FieldErrors>({
		trigger: null,
		fieldValueChanged: {
			fieldName: null
		},
		workItemIsAssignedTo: {
			identity: null 
		},
		workItemTransitions: {
			from: null,
			to: null,
		},
		checklistTrigger: {
			field: null
		}
	});

	const fieldValueChangedHooks = useFieldValueChanged();
	const workItemIsAssignedToHook = useAssignedToForm();
	const workItemTransitionsHook = useWorkItemTransition();
	const checklistTriggerHook = useCheckListTrigger();

	const changeValidationError = useCallback((error: Partial<FieldErrors>) => {
		setValidationErrors(oldErrors => ({
			...oldErrors,
			...error
		}));
	}, []);

	const changeTriggerData = useCallback((data: Partial<TriggerItemType>) => {
		setTriggerItem(oldData => ({
			...oldData as TriggerItemType,
			...data
		}));
	}, []);

	const resetValidations = useCallback(() => {
		setValidationErrors({
			trigger: null,
			fieldValueChanged: {
				fieldName: null
			},
			workItemIsAssignedTo: {
				identity: null 
			},
			workItemTransitions: {
				from: null,
				to: null,
			},
			checklistTrigger: {
				field: null
			}
		});
	}, []);

	const validate = useCallback(() => {
		let hasError = false;
		const formValidationErrors: FieldErrors = {
			trigger: null,
			fieldValueChanged: {
				fieldName: null
			},
			workItemIsAssignedTo: {
				identity: null 
			},
			workItemTransitions: {
				from: null,
				to: null,
			},
			checklistTrigger: {
				field: null
			}
		};
		
		const errorForTriggerItem = () => {
			return [
				stringRequired(trigger as string, 'This field is required'),
			];
		};

		const validations = errorForTriggerItem();
		const msg = validations.find(validation => validation);
		if (msg) {
			formValidationErrors.trigger = msg;
		}

		const runValidation = new Map()
			.set(triggerTypes.workItemTransitions, () => {
				const values = triggerItem!.workItemTransitions as WorkItemTransitionTriggerType;
				const results = workItemTransitionsHook.validate({
					selectedFrom: values.selectedFrom.map((item) => item?.toString() || ''),
					selectedTo: values.selectedTo.map((item) => item?.toString() || '') as string[]
				});

				if (results.hasErrors()) {
					hasError = true;
					formValidationErrors.workItemTransitions = {
						from: results.errors.from,
						to: results.errors.to
					};
				}
			})
			.set(triggerTypes.workItemIsAssignedTo, () => {
				const values = triggerItem!.workItemIsAssignedTo as WorkItemIsAssignedToTriggerType;
				const results = workItemIsAssignedToHook.validate({
					identity: values.selectedOption as string
				});

				if (results.hasErrors()) {
					hasError = true;
					formValidationErrors.workItemIsAssignedTo = {
						identity: results.errors.identity
					};
				}
			})
			.set(triggerTypes.fieldValueIsChanged, () => {
				const values = triggerItem!.fieldValueChange as FieldValueChangedTriggerType;
				const results = fieldValueChangedHooks.validate({
					fieldName: values.selectedOptions as string,
				});

				if (results.hasError()) {
					hasError = true;
					formValidationErrors.fieldValueChanged = {
						fieldName: results.errors.fieldName
					};
				}
			})
			.set(triggerTypes.checkListIsCompleted, () => {
				const values = triggerItem!.checklistTrigger as ChecklistTriggerType;
				const results = checklistTriggerHook.validate({
					field: values.field,
				});

				if (results.hasError()) {
					hasError = true;
					formValidationErrors.checklistTrigger = {
						field: results.errors.field
					};
				}
			})
			.set(triggerTypes.checkListIsNotCompleted, () => {
				const values = triggerItem!.checklistTrigger as ChecklistTriggerType;
				const results = checklistTriggerHook.validate({
					field: values.field,
				});

				if (results.hasError()) {
					hasError = true;
					formValidationErrors.checklistTrigger = {
						field: results.errors.field
					};
				}
			})
			.get(trigger);

		runValidation && runValidation();
		setValidationErrors(formValidationErrors);
		return hasError;
	}, [
		trigger,
		triggerItem?.fieldValueChange,
		triggerItem?.workItemIsAssignedTo?.selectedOption,
		triggerItem?.workItemTransitions?.selectedFrom,
		triggerItem?.workItemTransitions?.selectedTo,
		validationErrors
	]);

	const getValues = useCallback(() => {
		const getData = new Map()
			.set(triggerTypes.fieldValueIsChanged, () => {
				return fieldValueChangedHooks.getValues(triggerItem?.fieldValueChange as FieldValueChangedTriggerType);
			})
			.set(triggerTypes.workItemIsAssignedTo, () => {
				return workItemIsAssignedToHook.getValues(triggerItem?.workItemIsAssignedTo as WorkItemIsAssignedToTriggerType);
			})
			.set(triggerTypes.workItemTransitions, () => {
				return workItemTransitionsHook.getValues(triggerItem?.workItemTransitions as WorkItemTransitionTriggerType);
			})
			.set(triggerTypes.workItemIsCreated, () => {
				return null;
			})
			.set(triggerTypes.workItemIsUpdated, () => {
				return null;
			})
			.set(triggerTypes.checkListIsCompleted, () => {
				return checklistTriggerHook.getValues(triggerItem?.checklistTrigger as ChecklistTriggerType);
			})
			.set(triggerTypes.checkListIsNotCompleted, () => {
				return checklistTriggerHook.getValues(triggerItem?.checklistTrigger as ChecklistTriggerType);
			})
			.get(trigger);

		if (!getData) {
			return {
				type: trigger as string,
				data: null
			};
		}

		return {
			type: trigger as string,
			data: getData()
		};
	}, [triggerItem, trigger]);

	const reset = useCallback(() => {
		setTrigger('');
		setTriggerItem(null);
	}, []);

	const loadTrigger = useCallback(async (data: {type: string; values: any}) => {
		const loadValuesMap = new Map()
			.set(triggerTypes.fieldValueIsChanged, async (triggerType: string, data: FieldValueChangedInputDataType) => {
				setTriggerItem({
					...initialTriggerValues,
					fieldValueChange: await fieldValueChangedHooks.loadValues({
						projectId: applicationContext.projectId,
						data: data
					})
				});
				setTrigger(triggerType);
			})
			.set(triggerTypes.workItemIsAssignedTo, async (triggerType: string, data: WorkItemAssignedToInputDataType) => {
				setTriggerItem({
					...initialTriggerValues,
					workItemIsAssignedTo: await workItemIsAssignedToHook.loadValues({
						organizationName: applicationContext.organizationName,
						data: data
					})
				});
				setTrigger(triggerType);
			})
			.set(triggerTypes.workItemTransitions, async (triggerType: string, data: WorkItemTransitionsInputDataType) => {
				setTriggerItem({
					...initialTriggerValues,
					workItemTransitions: await workItemTransitionsHook.loadValues({
						organizationName: applicationContext.organizationName,
						projectId: applicationContext.projectId,
						data: data
					})
				});
				setTrigger(triggerType);
			})
			.set(triggerTypes.checkListIsCompleted, async (triggerType: string, data: ChecklistTriggerInputDataType) => {
				changeTriggerData({...initialTriggerValues, checklistTrigger: { field: data.field }});
				setTrigger(triggerType);
			})
			.set(triggerTypes.checkListIsNotCompleted, async (triggerType: string, data: ChecklistTriggerInputDataType) => {
				changeTriggerData({...initialTriggerValues, checklistTrigger: { field: data.field }});
				setTrigger(triggerType);
			})
			.set(triggerTypes.workItemIsCreated, async (triggerType: string) => {
				setTrigger(triggerType);
			})
			.set(triggerTypes.workItemIsUpdated, async (triggerType: string) => {
				setTrigger(triggerType);
			});

		const loadValues = loadValuesMap.get(data.type);
		if (loadValues) {
			await loadValues(data.type, data.values);
		}
	}, [applicationContext]);

	return (
		<AutomationRuleTriggerContext.Provider
			value={{
				trigger,
				setTrigger,
				validate,
				validationErrors,
				changeValidationError,
				resetValidations,
				triggerData: triggerItem,
				setTriggerData: setTriggerItem,
				changeTriggerData,
				getValues,
				reset,
				loadTrigger
			}}
		>
			{props?.children}
		</AutomationRuleTriggerContext.Provider>
	);
}